import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Save, Upload, Building2, Phone, Mail, MapPin, 
  FileText, CreditCard, QrCode, Wallet, Image, Loader2, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GarageProfile = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    mobile: '',
    address: '',
    city: '',
    gst_number: '',
    pan_number: '',
    owner_name: '',
    logo_url: '',
    upi_id: '',
    bank_name: '',
    account_number: '',
    ifsc_code: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const token = localStorage.getItem('garage_token');
    if (!token) {
      navigate('/garage/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/garage/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setProfile({
          name: data.garage.name || '',
          email: data.garage.email || '',
          mobile: data.garage.mobile || '',
          address: data.garage.address || '',
          city: data.garage.city || '',
          gst_number: data.garage.gst_number || '',
          pan_number: data.garage.pan_number || '',
          owner_name: data.garage.owner_name || '',
          logo_url: data.garage.logo_url || '',
          upi_id: data.garage.upi_id || '',
          bank_name: data.garage.bank_name || '',
          account_number: data.garage.account_number || '',
          ifsc_code: data.garage.ifsc_code || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const res = await fetch(`${API_URL}/api/garage/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profile.name,
          mobile: profile.mobile,
          address: profile.address,
          city: profile.city,
          gst_number: profile.gst_number,
          pan_number: profile.pan_number,
          owner_name: profile.owner_name,
          upi_id: profile.upi_id,
          bank_name: profile.bank_name,
          account_number: profile.account_number,
          ifsc_code: profile.ifsc_code
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Profile saved successfully!');
        localStorage.setItem('garage_profile', JSON.stringify(data.garage));
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/garage/dashboard/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        setProfile(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(data.detail || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} transition-colors duration-200`}>
      {/* Header */}
      <header className={`${theme.cardBg} border-b ${theme.border} px-4 py-3 sticky top-0 z-10`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/garage/dashboard')}
              className={`${theme.textSecondary} hover:${theme.textPrimary}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-lg font-bold ${theme.textPrimary}`}>Garage Profile</h1>
              <p className={`text-xs ${theme.textSecondary}`}>Manage your garage information</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Logo Section */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
              <Image className="w-5 h-5 text-orange-500" />
              Garage Logo
            </CardTitle>
            <p className={`text-sm ${theme.textSecondary}`}>
              This logo will appear on invoices, estimates, and emails sent to customers
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className={`w-32 h-32 rounded-lg border-2 border-dashed ${theme.border} flex items-center justify-center overflow-hidden ${theme.pageBg}`}>
                {profile.logo_url ? (
                  <img 
                    src={`${API_URL}/api${profile.logo_url}`} 
                    alt="Garage Logo" 
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="text-center">
                    <Image className={`w-8 h-8 mx-auto ${theme.textMuted}`} />
                    <p className={`text-xs ${theme.textMuted} mt-1`}>No logo</p>
                  </div>
                )}
                {profile.logo_url && (
                  <div className="hidden text-center">
                    <Image className={`w-8 h-8 mx-auto ${theme.textMuted}`} />
                    <p className={`text-xs ${theme.textMuted} mt-1`}>No logo</p>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className={`${theme.border} ${theme.textPrimary}`}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {profile.logo_url ? 'Change Logo' : 'Upload Logo'}
                </Button>
                <p className={`text-xs ${theme.textMuted} mt-2`}>
                  Recommended: Square image, PNG or JPG, max 5MB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
              <Building2 className="w-5 h-5 text-orange-500" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={theme.textSecondary}>Garage Name *</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  placeholder="Your Garage Name"
                  className={`${theme.inputBg} ${theme.textPrimary}`}
                />
              </div>
              <div className="space-y-2">
                <Label className={theme.textSecondary}>Owner Name</Label>
                <Input
                  value={profile.owner_name}
                  onChange={(e) => setProfile({...profile, owner_name: e.target.value})}
                  placeholder="Owner's Name"
                  className={`${theme.inputBg} ${theme.textPrimary}`}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={theme.textSecondary}>Address *</Label>
              <Textarea
                value={profile.address}
                onChange={(e) => setProfile({...profile, address: e.target.value})}
                placeholder="Full address including street, area, landmark"
                className={`${theme.inputBg} ${theme.textPrimary} min-h-[80px]`}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={theme.textSecondary}>City</Label>
                <Input
                  value={profile.city}
                  onChange={(e) => setProfile({...profile, city: e.target.value})}
                  placeholder="City"
                  className={`${theme.inputBg} ${theme.textPrimary}`}
                />
              </div>
              <div className="space-y-2">
                <Label className={theme.textSecondary}>Contact Number *</Label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
                  <Input
                    value={profile.mobile}
                    onChange={(e) => setProfile({...profile, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    placeholder="10-digit mobile number"
                    className={`pl-10 ${theme.inputBg} ${theme.textPrimary}`}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className={theme.textSecondary}>Email (Read-only)</Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
                <Input
                  value={profile.email}
                  disabled
                  className={`pl-10 ${theme.inputBg} ${theme.textMuted} opacity-60`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax Information */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
              <FileText className="w-5 h-5 text-orange-500" />
              Tax Information
            </CardTitle>
            <p className={`text-sm ${theme.textSecondary}`}>
              GST details will be shown on invoices if provided
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={theme.textSecondary}>GST Number</Label>
                <Input
                  value={profile.gst_number}
                  onChange={(e) => setProfile({...profile, gst_number: e.target.value.toUpperCase()})}
                  placeholder="e.g., 29ABCDE1234F1Z5"
                  className={`${theme.inputBg} ${theme.textPrimary}`}
                />
              </div>
              <div className="space-y-2">
                <Label className={theme.textSecondary}>PAN Number</Label>
                <Input
                  value={profile.pan_number}
                  onChange={(e) => setProfile({...profile, pan_number: e.target.value.toUpperCase()})}
                  placeholder="e.g., ABCDE1234F"
                  className={`${theme.inputBg} ${theme.textPrimary}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Settings */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
              <Wallet className="w-5 h-5 text-orange-500" />
              Payment Collection
            </CardTitle>
            <p className={`text-sm ${theme.textSecondary}`}>
              These details will be shown on invoices and emails for easy payment
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* UPI Section */}
            <div className={`p-4 rounded-lg ${theme.pageBg} border ${theme.border}`}>
              <div className="flex items-center gap-2 mb-3">
                <QrCode className="w-5 h-5 text-green-500" />
                <span className={`font-medium ${theme.textPrimary}`}>UPI Payment</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.textSecondary}>UPI ID</Label>
                  <Input
                    value={profile.upi_id}
                    onChange={(e) => setProfile({...profile, upi_id: e.target.value})}
                    placeholder="yourgarage@upi or phone@paytm"
                    className={`${theme.inputBg} ${theme.textPrimary}`}
                  />
                  <p className={`text-xs ${theme.textMuted}`}>
                    A QR code will be auto-generated for customers to scan and pay
                  </p>
                </div>
                {profile.upi_id && (
                  <div className="flex flex-col items-center">
                    <p className={`text-xs ${theme.textSecondary} mb-2`}>QR Preview</p>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=upi://pay?pa=${profile.upi_id}&pn=${encodeURIComponent(profile.name)}`}
                      alt="UPI QR Code"
                      className="w-24 h-24 rounded-lg border border-green-500/50"
                    />
                    <p className={`text-xs ${theme.textMuted} mt-1`}>{profile.upi_id}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bank Details Section */}
            <div className={`p-4 rounded-lg ${theme.pageBg} border ${theme.border}`}>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-blue-500" />
                <span className={`font-medium ${theme.textPrimary}`}>Bank Transfer</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.textSecondary}>Bank Name</Label>
                  <Input
                    value={profile.bank_name}
                    onChange={(e) => setProfile({...profile, bank_name: e.target.value})}
                    placeholder="e.g., HDFC Bank"
                    className={`${theme.inputBg} ${theme.textPrimary}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={theme.textSecondary}>Account Number</Label>
                  <Input
                    value={profile.account_number}
                    onChange={(e) => setProfile({...profile, account_number: e.target.value.replace(/\D/g, '')})}
                    placeholder="Account Number"
                    className={`${theme.inputBg} ${theme.textPrimary}`}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className={theme.textSecondary}>IFSC Code</Label>
                  <Input
                    value={profile.ifsc_code}
                    onChange={(e) => setProfile({...profile, ifsc_code: e.target.value.toUpperCase()})}
                    placeholder="e.g., HDFC0001234"
                    className={`${theme.inputBg} ${theme.textPrimary}`}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview Section */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
              <Check className="w-5 h-5 text-green-500" />
              Profile Preview
            </CardTitle>
            <p className={`text-sm ${theme.textSecondary}`}>
              This is how your information will appear on invoices and emails
            </p>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-lg border ${theme.border} ${theme.pageBg}`}>
              <div className="flex items-start gap-4">
                {profile.logo_url ? (
                  <img 
                    src={`${API_URL}/api${profile.logo_url}`} 
                    alt="Logo" 
                    className="w-16 h-16 object-contain rounded"
                  />
                ) : (
                  <div className={`w-16 h-16 rounded ${theme.cardBg} flex items-center justify-center border ${theme.border}`}>
                    <Building2 className={`w-6 h-6 ${theme.textMuted}`} />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className={`font-bold ${theme.textPrimary}`}>{profile.name || 'Garage Name'}</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>{profile.address || 'Address'}</p>
                  <p className={`text-sm ${theme.textSecondary}`}>{profile.city}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                    {profile.mobile && (
                      <span className={theme.textSecondary}>
                        <Phone className="w-3 h-3 inline mr-1" />
                        {profile.mobile}
                      </span>
                    )}
                    {profile.gst_number && (
                      <span className={theme.textSecondary}>
                        GST: {profile.gst_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button (Mobile) */}
        <div className="md:hidden pb-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GarageProfile;
